import React, { useState } from "react";
import Sidebar from "../../components/Sidebar";
import "./bank_statement_company.css";
import {
  FaCalendarAlt,
  FaSave,
  FaSpinner,
  FaHashtag,
  FaPoundSign,
  FaUniversity,
  FaCloudUploadAlt,
  FaTimes,
  FaCheckCircle,
  FaTag,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createBankStatementApi,
  takeImageUploadExpenseApi,
} from "../../services/apiCall";

export default function BankStatementCompanyPage() {
  const [date, setDate] = useState("");
  const [description, setDescription] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [typeId, setTypeId] = useState("");
  const [typeName, setTypeName] = useState("");
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState("");
  const [category, setCategory] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);

  const businessName =
    useSelector((state) => state?.user?.businessName) || "Your Business";
  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    setImage(file);
    setIsExtracting(true);
    setExtracted(false);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", "bank");

    try {
      const response = await takeImageUploadExpenseApi(formData);
      const data = response?.data?.data;

      if (data) {
        if (data.amount) setAmount(data.amount.toString());
        if (data.balance) setBalance(data.balance.toString());
        if (data.referenceNumber) setReferenceNumber(data.referenceNumber);
        if (data.category) setCategory(data.category);
        if (data.description) setDescription(data.description);
        if (data.transactionType) {
          const t = data.transactionType.toUpperCase();
          setTypeName(t);
          setTypeId(t === "CREDIT" ? "1" : "2");
        }
        if (data.createdAt)
          setDate(new Date(data.createdAt).toISOString().split("T")[0]);
        setExtracted(true);
        toast.success("Bank doc scanned — fields auto-filled!");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error);
      toast.error("Failed to extract data from document.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!amount || !balance || !referenceNumber) {
      toast.error("Amount, Balance and Reference Number are required.");
      return;
    }
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      toast.error("Amount must be a positive number.");
      return;
    }

    setIsSaving(true);
    const payload = {
      businessId,
      transactionDate: date,
      referenceNumber,
      transactionType: typeName,
      amount,
      balance,
      category,
      description,
      accountId: "121212",
      createdBy: userId,
    };

    try {
      const response = await createBankStatementApi(payload);
      if (response?.data?.statement != null) {
        toast.success("Bank statement saved!");
        setDate(""); setDescription(""); setReferenceNumber("");
        setTypeId(""); setTypeName(""); setAmount("");
        setBalance(""); setCategory("");
        setImage(null); setImagePreview(null); setExtracted(false);
      }
    } catch (err) {
      toast.error(err?.message ?? "Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTypeSelect = (val) => {
    setTypeId(val);
    setTypeName(val === "1" ? "CREDIT" : val === "2" ? "DEBIT" : "");
  };

  const today = new Date().toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        {/* Header */}
        <header className="page-header">
          <div>
            <h2 className="page-title">Bank Statement</h2>
            <p className="page-sub">Record a new bank transaction</p>
          </div>
          <div className="page-info">
            <h3 className="company-name">{businessName}</h3>
            <p className="today">{today}</p>
          </div>
        </header>

        {/* Card */}
        <section className="statement-card">
          <div className="card-header-strip">
            <FaUniversity />
            <h3>Transaction Details</h3>
          </div>

          <div className="statement-grid">
            {/* Date */}
            <div className="field">
              <label>Date <span className="req">*</span></label>
              <div className="icon-input">
                <FaCalendarAlt className="input-icon" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            </div>

            {/* Reference Number */}
            <div className="field">
              <label>Reference Number <span className="req">*</span></label>
              <div className="icon-input">
                <FaHashtag className="input-icon" />
                <input
                  type="text"
                  placeholder="e.g. REF12345"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                />
              </div>
            </div>

            {/* Type toggle */}
            <div className="field">
              <label>Transaction Type</label>
              <div className="type-toggle">
                <button
                  type="button"
                  className={`type-btn credit ${typeId === "1" ? "active" : ""}`}
                  onClick={() => handleTypeSelect("1")}
                >
                  ↑ CREDIT
                </button>
                <button
                  type="button"
                  className={`type-btn debit ${typeId === "2" ? "active" : ""}`}
                  onClick={() => handleTypeSelect("2")}
                >
                  ↓ DEBIT
                </button>
              </div>
            </div>

            {/* Category */}
            <div className="field">
              <label>Category</label>
              <div className="icon-input">
                <FaTag className="input-icon" />
                <input
                  type="text"
                  placeholder="e.g. Office Supplies"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </div>
            </div>

            {/* Amount */}
            <div className="field">
              <label>Amount <span className="req">*</span></label>
              <div className="icon-input">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
            </div>

            {/* Balance */}
            <div className="field">
              <label>Balance <span className="req">*</span></label>
              <div className="icon-input">
                <FaPoundSign className="input-icon" />
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                />
              </div>
            </div>

            {/* Description */}
            <div className="field full">
              <label>Description (optional)</label>
              <textarea
                placeholder="Enter description…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Upload */}
            <div className="field full">
              <label>Attach Bank Statement Document</label>
              <div className="upload-zone">
                <input
                  type="file"
                  accept="image/*,.pdf,.csv,.xlsx"
                  onChange={handleImageChange}
                />
                <div className="upload-icon-wrap">
                  {isExtracting ? (
                    <FaSpinner className="spin" style={{ color: "#fff", fontSize: "1.2rem" }} />
                  ) : (
                    <FaCloudUploadAlt />
                  )}
                </div>
                <h4>{image ? image.name : "Upload bank statement"}</h4>
                <p>Drag & drop or click to browse</p>
                <span className="upload-hint">Supports: JPG, PNG, PDF, CSV, XLSX</span>
                {isExtracting && (
                  <div className="extracting-badge">
                    <FaSpinner className="spin" /> Extracting data…
                  </div>
                )}
                {extracted && !isExtracting && (
                  <div className="extracting-badge">
                    <FaCheckCircle /> Fields auto-filled!
                  </div>
                )}
              </div>

              {imagePreview && (
                <div className="receipt-preview">
                  <img src={imagePreview} alt="Document preview" />
                  <button
                    className="remove-img"
                    onClick={() => { setImage(null); setImagePreview(null); setExtracted(false); }}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="card-footers">
            <button
              className="save-btns"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? (
                <><FaSpinner className="spin" /> Saving…</>
              ) : (
                <><FaSave /> Save Bank Statement</>
              )}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
